export function adaptQueryParams<F extends MySsFilter, P extends MySsFilterParams>(
  filters: F,
  { page, pageSize } = PAGINATION,
  callBack?: MySsMethod,
): P {
  let filterParams: P = {
    filteredField: filters.filteredField,
    genericTerm: filters.genericTerm,
    page,
    pageSize,
    sort: 'desc',
    sortName: 'billingDate',
  } as P;

  validateFilters(filters);

  if (hasDateFilters(filters.date)) filterParams = { ...filterParams, ...adaptDateParams(filters.date) };

  if (hasDateFilters(filters.paymentDate))
    filterParams = { ...filterParams, ...adaptPaymentDateParams(filters.paymentDate) };

  if (hasFilteredField(filters.filteredField))
    filterParams = { ...filterParams, genericTerm: adaptGenericTerm(filters) };

  if (!callBack) return filterParams;
  return callBack({ ...filterParams, ...omit(filters, ['filteredField', 'genericTerm', 'date']) } as P);

  function adaptGenericTerm({ filteredField, genericTerm }: F): string {
    if (documentFilteredFields().includes(filteredField)) {
      const companyDocument = CompanyDocument(genericTerm);
      const personalDocument = PersonalDocument(genericTerm);

      if (companyDocument.isValid) return companyDocument.unmasked;

      if (personalDocument.isValid) return personalDocument.unmasked;
    }

    return genericTerm;
  }

  function adaptDateParams(date: Date[]) {
    const dateStart = DateTime.fromJSDate(date[0]);
    const dateEnd = date[1] ? DateTime.fromJSDate(date[1]) : dateStart;

    return {
      dateStart: dateStart.toISODate(),
      dateEnd: dateEnd.toISODate(),
    };
  }

  function adaptPaymentDateParams(date: Date[]) {
    const dateStart = DateTime.fromJSDate(date[0]);
    const dateEnd = date[1] ? DateTime.fromJSDate(date[1]) : dateStart;

    return {
      paymentDateStart: dateStart.toISODate(),
      paymentDateEnd: dateEnd.toISODate(),
    };
  }

  function validateFilters(filters: F): void {
    if (isNullOrEmptyObject(filters)) {
      throwFilterError([ERRORS.MY_SCANSOURCE_UI.FILTERS_CANNOT_BE_EMPTY]);
    }

    const { filteredField, genericTerm, date } = filters;

    if (hasFilteredField(filteredField)) validateGenericTherm(filteredField, genericTerm);

    if (hasDateFilters(date)) validateDateField(date);
  }

  function hasFilteredField(filteredField: F['filteredField']): boolean {
    return !isNullOrEmptyObject(filteredField) && !isEmptyString(String(filteredField));
  }

  function hasDateFilters(date: any): date is Array<Date> {
    if (!date) return false;

    return Array.isArray(date) && !!date.length;
  }

  function validateGenericTherm(filteredField: string, genericTerm: string): void {
    if (isNullOrEmptyObject(genericTerm) || isEmptyString(genericTerm))
      throwFilterError([ERRORS.MY_SCANSOURCE_UI.GENERIC_TERM_REQUIRED]);
    if (documentFilteredFields().includes(filteredField)) {
      const companyDocument = CompanyDocument(genericTerm);
      const personalDocument = PersonalDocument(genericTerm);

      if (companyDocument.isCompanyDocumentLike && !companyDocument.isValid)
        throwFilterError([ERRORS.MY_SCANSOURCE_UI.INVALID_COMPANY_DOCUMENT]);
      if (personalDocument.isPersonalDocument && !personalDocument.isValid)
        throwFilterError([ERRORS.MY_SCANSOURCE_UI.INVALID_DOCUMENT]);
      if (!personalDocument.isValid && !companyDocument.isValid)
        throwFilterError([ERRORS.MY_SCANSOURCE_UI.SHOULD_BE_A_COMPANY_DOCUMENT]);
    }
  }

  function validateDateField(date: Date[]) {
    const [start, end] = date;

    if (!(start instanceof Date)) throwInternalError([ERRORS.MY_SCANSOURCE_UI.INTERNAL.INVALID_DATE_START]);

    if (end && !(end instanceof Date))
      throwInternalError([ERRORS.MY_SCANSOURCE_UI.INTERNAL.INVALID_DATE_END]);

    if (!!end && start > end)
      throwFilterError([ERRORS.MY_SCANSOURCE_UI.END_DATE_CANNOT_BE_BEFORE_START_DATE]);
  }

  function throwFilterError(errorMessages: MySsException['messages']) {
    const error: MySsException = {} as MySsException;

    error.severity = 'warn';
    error.messages = [...errorMessages];
    error.status = 6400;

    throw new Error(JSON.stringify(error));
  }

  function throwInternalError(errorMessages: MySsException['messages']) {
    const error: MySsException = {} as MySsException;

    error.severity = 'error';
    error.messages = [...errorMessages];
    error.status = 6500;

    throw new Error(JSON.stringify(error));
  }

  function documentFilteredFields(): string[] {
    return [
      'FINAL_CUSTOMER_DOCUMENT',
      'COMPANY_DOCUMENT',
      'PERSONAL_DOCUMENT',
      'DOCUMENT',
      'document',
      'companyDocument',
      'finalCustomerDocument',
      'personalDocument',
    ];
  }
}

export function removeEmptyFields<T extends MySsFilterParams>(...fields: (keyof T)[]) {
  return (obj: T) =>
    fields.reduce((acc, field) => {
      if (isEmptyData(obj[field])) delete acc[field];

      return acc;
    }, obj);
}
